import { FormProvider, Input, Loader, useForm } from '@qred/components-library';
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ValidationContext } from '~/components/hoc/withValidation';
import { FormErrorBanner } from '~/components/shared/FormErrorBanner/FormErrorBanner';
import StepHeader from '~/components/shared/Stepper/components/StepHeader';
import { TurnoverSelect } from '~/components/shared/TurnoverSelect/TurnoverSelect';
import { ClientType, FlowTypes } from '~/enums';
import {
  areSameCompanies,
  getClientIdByOrgNumber,
  purposeOfLoanIsValid,
} from '~/helpers/utils';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { RootState } from '~/store';
import {
  resetCompanyInForm,
  setCompanyIsNew,
  updateApplicantData,
  updateForm,
} from '~/store/slices/loanApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import {
  StandardStepContent,
  StandardStepContentBody,
  StandardStepContentFooter,
} from '~/styles/BaseStyledComponents';
import {
  StartApplicationForm,
  startApplicationSchema,
} from './StartApplication.schema';
import { CompanySearch } from './components/CompanySearch';
import { CompanySelect } from './components/CompanySelect';
import { ForeignCurrencyIncome } from './components/ForeignCurrencyIncome';
import { LoanPurposeSelect } from './components/LoanPurposeSelect';
import { StartApplicationFooter } from './components/StartApplicationFooter';

export const StartApplication = () => {
  const {
    form: {
      organizationNumber,
      companyType,
      companyName,
      purposeOfLoan,
      reason,
      turnover: formTurnover,
      incomeReceivedInForeignCurrencies: formIncomeReceivedInForeignCurrencies,
    },
    overview: { clients },
    apiStatus,
    companyIsNew: initialCompanyIsNew,
    flowType,
  } = useSelector((state: RootState) => state.loanApplication);

  const validationContext = useContext(ValidationContext);
  const dispatch = useAppDispatch();

  const formMethods = useForm<StartApplicationForm>({
    schema: startApplicationSchema,
    mode: 'onTouched',
    defaultValues: {
      companyIsNew: flowType === FlowTypes.Manual ? true : initialCompanyIsNew,
      clientId: organizationNumber
        ? getClientIdByOrgNumber(organizationNumber, clients)
        : undefined,
      companyDetails:
        organizationNumber &&
        !getClientIdByOrgNumber(organizationNumber, clients)
          ? {
              companyName,
              companyType,
              companyNumber: organizationNumber || undefined,
            }
          : undefined,
      loanPurpose: {
        purposeOfLoan: purposeOfLoanIsValid(purposeOfLoan)
          ? purposeOfLoan
          : undefined,
        reason: reason,
      },
      turnover: formTurnover,
      incomeReceivedInForeignCurrencies: formIncomeReceivedInForeignCurrencies,
    },
  });

  const {
    watch,
    formState: { errors, isValid },
  } = formMethods;

  const clientId = watch('clientId');
  const companyIsNew = watch('companyIsNew');
  const companyDetails = watch('companyDetails');
  const loanPurpose = watch('loanPurpose');
  const turnover = watch('turnover');
  const incomeReceivedInForeignCurrencies = watch(
    'incomeReceivedInForeignCurrencies'
  );

  const handleCompanySelect = (selectedClientId: string) => {
    const newCompanySelected = selectedClientId === 'other';
    dispatch(resetCompanyInForm());

    if (newCompanySelected) {
      formMethods.setValue('clientId', undefined);
      formMethods.setValue('companyIsNew', true, { shouldValidate: true });
    } else {
      formMethods.setValue('clientId', selectedClientId);
      formMethods.setValue('companyIsNew', false, { shouldValidate: true });
      formMethods.unregister('companyDetails');
    }
  };

  useEffect(() => {
    if (!isValid) {
      validationContext.manuallyInvalidateForm(true);
    }

    return () => {
      validationContext.manuallyInvalidateForm(false);
    };
  }, [isValid]);

  useEffect(() => {
    if (apiStatus.clients === ApiStatus.Completed) {
      const orgNumberExistsInListOfClients = clients.some((c) =>
        areSameCompanies(organizationNumber, c.company_no)
      );
      const _companyIsNew =
        clients.length === 0 ||
        (Boolean(organizationNumber) && !orgNumberExistsInListOfClients);
      formMethods.setValue('companyIsNew', _companyIsNew);
    }
  }, [apiStatus.clients, clients]);

  useEffect(() => {
    if (isValid) {
      dispatch(setCompanyIsNew(companyIsNew));

      if (clientId) {
        const client = clients.find(
          (_client) => _client.client_id === Number(clientId)
        );

        if (client) {
          dispatch(
            updateForm({
              organizationNumber: client.company_no ?? '',
              companyType: client.company_type ?? '',
              clientType: client.active ? ClientType.RLC : ClientType.NLC,
              companyName: client.company_name,
            })
          );
          dispatch(
            updateApplicantData({
              email: client.email ?? '',
              phone: client.phone ?? '',
            })
          );
        }
      } else if (companyDetails) {
        dispatch(
          updateForm({
            organizationNumber: companyDetails.companyNumber,
            companyType: companyDetails.companyType,
            companyName: companyDetails.companyName,
          })
        );
      }

      dispatch(
        updateForm({
          purposeOfLoan: loanPurpose.purposeOfLoan,
          reason: loanPurpose.reason,
          turnover,
          incomeReceivedInForeignCurrencies: incomeReceivedInForeignCurrencies,
        })
      );
    }
  }, [
    isValid,
    clientId,
    companyDetails,
    loanPurpose.purposeOfLoan,
    loanPurpose.reason,
    turnover,
    incomeReceivedInForeignCurrencies,
    dispatch,
  ]);

  return (
    <StandardStepContent>
      <StandardStepContentBody>
        <StepHeader
          title="Onboarding.StartApplicationTitle"
          description="Onboarding.StartApplicationDescription"
        />
        <FormProvider {...formMethods}>
          <Input.Group orientation="vertical">
            {apiStatus.clients === ApiStatus.Started ? (
              <Loader />
            ) : (
              <>
                {clients.length > 0 && (
                  <CompanySelect
                    clients={clients}
                    companyIsNew={companyIsNew}
                    handleCompanySelect={handleCompanySelect}
                  />
                )}
                {companyIsNew && <CompanySearch />}
              </>
            )}
            <LoanPurposeSelect />
            <TurnoverSelect />
          </Input.Group>
          <ForeignCurrencyIncome />
        </FormProvider>
        <FormErrorBanner errors={errors} />
      </StandardStepContentBody>
      <StandardStepContentFooter>
        <StartApplicationFooter />
      </StandardStepContentFooter>
    </StandardStepContent>
  );
};
